import '@material/web/textfield/outlined-text-field.js';
import '@material/web/button/filled-button.js';
import '@material/web/button/text-button.js';

import { api, type Member } from '../api.js';
import { ce, el, clear, avatarEl, type TextFieldEl } from '../dom.js';
import { langSwitcher, t } from '../i18n.js';
import { icons } from '../icons.js';

export function renderPicker(
  members: Member[],
  onLogin: (member: Member) => void,
  onDisplay: () => void,
): HTMLElement {
  const root = el('div', {});
  const error = el('p', { class: 'error' });

  function login(member: Member, pin?: string): void {
    const body: Record<string, unknown> = { memberId: member.id };
    if (pin) body.pin = pin;
    api
      .post<{ member: Member }>('/api/session', body)
      .then((res) => onLogin(res.member))
      .catch((err: Error) => {
        error.textContent = err.message;
      });
  }

  function showPinPrompt(member: Member): void {
    clear(root);
    const backBtn = ce('md-text-button', { type: 'button' }, t('pickerBack'));
    backBtn.addEventListener('click', () => location.reload());
    const pin = ce('md-outlined-text-field', {
      id: 'pin-input',
      label: t('pickerEnterPin'),
      type: 'password',
      inputmode: 'numeric',
    }) as TextFieldEl;
    // `autofocus` does nothing on a custom element; focus it once Material has
    // upgraded and rendered its inner input.
    void customElements.whenDefined('md-outlined-text-field').then(() => pin.focus());
    root.append(
      el('h1', {}, t('pickerHi', { name: member.name })),
      el(
        'div',
        { class: 'card' },
        el(
          'form',
          {
            onsubmit: (ev: Event) => {
              ev.preventDefault();
              login(member, pin.value);
            },
          },
          pin,
          error,
          el(
            'div',
            { class: 'row' },
            ce('md-filled-button', { type: 'submit' }, t('pickerUnlock')),
            backBtn,
          ),
        ),
      ),
    );
    pin.focus();
  }

  root.append(
    el('div', { class: 'spread' }, el('h1', {}, t('pickerWho')), langSwitcher()),
    el(
      'div',
      { class: 'picker' },
      ...members.map((m) =>
        el(
          'div',
          {
            class: 'profile',
            role: 'button',
            tabindex: '0',
            'data-member': m.name,
            onclick: () => (m.hasPin ? showPinPrompt(m) : login(m)),
          },
          avatarEl(m.name, m.avatar, m.color, 56),
          el('div', {}, m.name),
          m.role === 'admin' ? el('span', { class: 'pill' }, t('pillAdmin')) : null,
        ),
      ),
      // The shared-display profile (ADR-0049): entered like any profile, but
      // it's a device, not a person — so a distinct card, not an avatar.
      el(
        'div',
        {
          class: 'profile profile-display',
          role: 'button',
          tabindex: '0',
          'data-display-profile': 'true',
          onclick: () => onDisplay(),
          onkeydown: (ev: Event) => {
            const key = (ev as KeyboardEvent).key;
            if (key === 'Enter' || key === ' ') {
              ev.preventDefault();
              onDisplay();
            }
          },
        },
        el('span', { class: 'avatar profile-display-icon', 'aria-hidden': 'true' }, icons.display()),
        el('div', {}, t('pickerSharedDisplay')),
      ),
    ),
    error,
  );
  return root;
}
